import { useState } from 'react';

import { TextButton } from '@components';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';

import { isEmail } from '@utils';

export const ForgotPasswordForm: React.FC<{
  username: string;
  isLoading: boolean;
  onChangeUsername: (value: string) => void;
  onBack: () => void;
  onSend: () => void;
}> = ({ username, isLoading, onChangeUsername, onBack, onSend }) => {
  const [emailError, setEmailError] = useState(false);

  const isSubmitDisabled = isLoading || !username.trim();

  const onSendClick = () => {
    if (!isEmail(username.trim())) {
      setEmailError(true);
      return;
    }

    setEmailError(false);
    onSend();
  };

  return (
    <>
      <Box display="flex" alignItems="center" mb={{ xs: 2, md: 2 }}>
        <Typography variant="h2">Reset your password</Typography>
      </Box>
      <Box
        sx={{
          bgcolor: '#feebb5',
          mb: { xs: 2, md: 4, marginLeft: '-58px', marginRight: '-58px' },
          padding: '16px 58px',
        }}
      >
        <Typography variant="h3">
          We've updated our authorization security 🔐{' '}
        </Typography>
        <Typography variant="body2">
          All sessions have been logged out.
        </Typography>
        <Typography variant="body2">
          Please reset your password once to continue
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        width="100%"
        maxWidth={{ xs: '100%', md: '320px' }}
        mb={4}
      >
        <TextField
          name="username"
          label="Email"
          variant="outlined"
          fullWidth
          type="email"
          value={username}
          error={emailError}
          helperText={
            emailError ? 'Please enter your email address' : undefined
          }
          onChange={(e) => {
            setEmailError(false);
            onChangeUsername(e.target.value);
          }}
          placeholder="name@company.com"
        />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <Button
            variant="contained"
            onClick={onSendClick}
            disabled={isSubmitDisabled}
          >
            {isLoading && (
              <CircularProgress
                size={16}
                color="secondary"
                sx={{ marginRight: 1 }}
              />
            )}
            Send email
          </Button>
          <TextButton variant="secondary" onClick={onBack}>
            ← Back to login
          </TextButton>
        </Box>
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        <Typography variant="body2" color="text.lightGrey">
          You will receive an email with instructions and a link to reset your
          password.
        </Typography>
      </Box>
    </>
  );
};
